/**
 * Utilitários para verificação de permissões
 */

import { ROLE_PERMISSIONS, isSystemAdmin } from "@/constants/userRoles";
import store from "@/store";

/**
 * Verifica se o usuário atual tem uma permissão específica
 * @param {string} permission - Nome da permissão a verificar
 * @returns {boolean}
 */
export function hasPermission(permission) {
  const user = store.state.auth.user;

  if (!user) {
    return false;
  }

  // Administradores gerais do sistema têm todas as permissões
  if (isSystemAdmin(user.is_admin)) {
    return true;
  }

  // Verifica permissões baseadas no perfil
  const rolePermissions = ROLE_PERMISSIONS[user.codigo_perfil] || [];
  return rolePermissions.includes(permission);
}

/**
 * Verifica se o usuário tem qualquer uma das permissões fornecidas
 * @param {string[]} permissions - Array de permissões
 * @returns {boolean}
 */
export function hasAnyPermission(permissions) {
  return permissions.some((permission) => hasPermission(permission));
}

/**
 * Verifica se o usuário tem todas as permissões fornecidas
 * @param {string[]} permissions - Array de permissões
 * @returns {boolean}
 */
export function hasAllPermissions(permissions) {
  return permissions.every((permission) => hasPermission(permission));
}

/**
 * Verifica se o usuário tem um perfil específico
 * @param {number} codigoPerfil - Código do perfil
 * @returns {boolean}
 */
export function hasRole(codigoPerfil) {
  const user = store.state.auth.user;
  return user && user.codigo_perfil === codigoPerfil;
}

/**
 * Verifica se o usuário tem qualquer um dos perfis fornecidos
 * @param {number[]} roles - Array de códigos de perfil
 * @returns {boolean}
 */
export function hasAnyRole(roles) {
  const user = store.state.auth.user;
  return user && roles.includes(user.codigo_perfil);
}

/**
 * Verifica se o usuário é administrador geral do sistema
 * @returns {boolean}
 */
export function isSystemAdministrator() {
  const user = store.state.auth.user;
  return user && isSystemAdmin(user.is_admin);
}

/**
 * Verifica se o usuário tem acesso a uma rota específica
 * @param {string} routeName - Nome da rota
 * @returns {boolean}
 */
export function canAccessRoute(routeName) {
  // Mapeamento de rotas para permissões necessárias
  const routePermissions = {
    home: ["access_home"],
    profile: ["access_profile"],
    library: ["access_library"],
    // shop: ["access_shop"],
    content: ["view_content", "manage_content"],
    courses: ["access_courses", "manage_courses"],
    video: ["access_videos"],
    simulated: ["access_simulated"],
    book: ["access_library"],
    academic: ["view_dashboard"],
    financial: ["view_reports"],
    sales: ["view_reports"],
    mycart: ["view_mycart"],
    checkout: ["view_checkout"],
    customizeprofile: ["view_customizeprofile"],
    manage: ["view_manage"],
    system: ["view_system_panel"], // Apenas para is_admin = 1
  };

  const requiredPermissions = routePermissions[routeName];

  if (!requiredPermissions) {
    return true; // Se não há permissões definidas, permite acesso
  }

  return hasAnyPermission(requiredPermissions);
}

/**
 * Obtém o nome do perfil do usuário atual
 * @returns {string}
 */
export function getCurrentRoleName() {
  const user = store.state.auth.user;
  if (!user) return "Não autenticado";

  if (isSystemAdmin(user.is_admin)) {
    return "Administrador Geral";
  }

  const roleNames = {
    1: "Administrador Institucional",
    2: "Designer Instrucional",
    8: "Usuário Comum",
  };

  return roleNames[user.codigo_perfil] || "Desconhecido";
}

/**
 * Verifica se o usuário pode gerenciar outros usuários
 * @returns {boolean}
 */
export function canManageUsers() {
  return hasPermission("manage_users") || isSystemAdministrator();
}

/**
 * Verifica se o usuário pode acessar o painel do sistema
 * @returns {boolean}
 */
export function canAccessSystemPanel() {
  return hasPermission("view_system_panel") || isSystemAdministrator();
}
